import styled, { createGlobalStyle, css, keyframes } from 'styled-components';

import { Text } from '../../../../components/Text/Text';
import { ANIM, ART_STYLE, COLORS, HOVER_CAPABLE, MOBILE, REDUCED_MOTION, SPACE } from '../../../../theme/tokens';

export const Root = styled.nav`
  position: relative;
  min-width: 0;
`;

/**
 * How faded each end of the row is (0 none, 1 full), registered so a change animates: an end fades in and out with its
 * arrow instead of snapping (a mask cannot transition from one gradient to another). Browsers without registered
 * properties switch at once.
 */
export const StripFadeProperties = createGlobalStyle`
  @property --art-strip-fade-start {
    syntax: '<number>';
    inherits: false;
    initial-value: 0;
  }
  @property --art-strip-fade-end {
    syntax: '<number>';
    inherits: false;
    initial-value: 0;
  }
`;

/**
 * The row's faded ends (the shared scrollFadeMaskX, with a clear band first): an end with more cards past it is fully
 * clear for `clear` px, under its edge arrow, then fades in, so the arrow reads against the page instead of a card's
 * artwork. Each end's strength rides --art-strip-fade-start / -end. The stops are mask alpha, not colours.
 */
const edgeMask = (clear: number): ReturnType<typeof css> => {
  const solid = clear + ART_STYLE.STRIP_FADE_PX;
  const start = 'rgb(0 0 0 / calc(1 - var(--art-strip-fade-start)))'; // design-allow: mask alpha
  const end = 'rgb(0 0 0 / calc(1 - var(--art-strip-fade-end)))'; // design-allow: mask alpha
  const gradient = `linear-gradient(to right, ${start} 0, ${start} ${clear}px, #000 ${solid}px, #000 calc(100% - ${solid}px), ${end} calc(100% - ${clear}px), ${end} 100%)`; // design-allow: mask alpha
  return css`
    -webkit-mask-image: ${gradient};
    mask-image: ${gradient};
  `;
};

/**
 * One row of cards that scrolls sideways once it outgrows the page, fading at an edge only while more cards
 * lie past it. The padding (undone by the negative margin) keeps the selected ring and the hover lift inside
 * the scroller, which clips both axes once it scrolls on one.
 */
export const Scroller = styled.div<{ $fadeStart: boolean; $fadeEnd: boolean }>`
  display: flex;
  align-items: stretch;
  gap: ${SPACE[12]};
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scroll-snap-type: x proximity;
  padding: ${SPACE[4]};
  margin: -${SPACE[4]};
  /* No scrollbar: the edge arrows (and a swipe or trackpad) move the row. */
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  /* Snap edges inside the padding: snapped to the scroller's own edge, the first card rested 4px into the scroll, so the
     start never read as reached (its arrow and fade stayed). */
  scroll-padding-inline: ${SPACE[4]};
  --art-strip-fade-start: ${({ $fadeStart }) => ($fadeStart ? 1 : 0)};
  --art-strip-fade-end: ${({ $fadeEnd }) => ($fadeEnd ? 1 : 0)};
  transition:
    --art-strip-fade-start ${ART_STYLE.STRIP_EDGE_MS}ms ${ANIM.EASE},
    --art-strip-fade-end ${ART_STYLE.STRIP_EDGE_MS}ms ${ANIM.EASE};
  ${edgeMask(ART_STYLE.STRIP_FADE_CLEAR_PX)}

  ${REDUCED_MOTION} {
    transition: none;
  }

  ${MOBILE} {
    gap: ${SPACE[8]};
    /* No clear band on a phone (it would eat a narrow card): the arrow's own filled button reads over the fade. */
    ${edgeMask(0)}
  }
`;

const join = keyframes`
  from {
    opacity: 0;
    transform: translateY(6px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

const cardBox = css`
  position: relative;
  flex: 0 0 ${ART_STYLE.CARD_WIDTH_PX}px;
  width: ${ART_STYLE.CARD_WIDTH_PX}px;
  scroll-snap-align: start;

  ${MOBILE} {
    flex-basis: ${ART_STYLE.CARD_WIDTH_MOBILE_PX}px;
    width: ${ART_STYLE.CARD_WIDTH_MOBILE_PX}px;
  }
`;

/** The kebab sits over the thumbnail's corner; it shows on hover, on keyboard focus and on the selected card
 *  (so on a touch screen, a tap selects the card and reveals its menu). */
export const MenuSlot = styled.div<{ $pinned: boolean }>`
  position: absolute;
  top: ${SPACE[8]};
  right: ${SPACE[8]};
  opacity: ${({ $pinned }) => ($pinned ? 1 : 0)};
  visibility: ${({ $pinned }) => ($pinned ? 'visible' : 'hidden')};
  transition:
    opacity ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    visibility ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
`;

/** One style card, lifting a touch on hover (its menu with it). A card that joins after the strip first
 *  showed (a new style, a duplicate) fades in. */
export const Card = styled.div<{ $fresh: boolean }>`
  ${cardBox}
  transition: transform ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
  ${({ $fresh }) =>
    $fresh
      ? css`
          animation: ${join} ${ART_STYLE.CARD_ENTER_MS}ms ${ANIM.EASE} backwards;
        `
      : ''}

  /* A mouse only: a tap on a phone would leave the card lifted. */
  ${HOVER_CAPABLE} {
    &:hover {
      transform: translateY(-${ART_STYLE.CARD_LIFT_PX}px);
    }
  }

  &:hover ${MenuSlot}, &:focus-within ${MenuSlot} {
    opacity: 1;
    visibility: visible;
  }

  ${REDUCED_MOTION} {
    animation: none;

    &:hover {
      transform: none;
    }
  }
`;

export const Select = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  padding: ${SPACE[4]};
  border-radius: 14px;
  border: 1px solid ${({ $selected }) => ($selected ? COLORS.ctaSolid : COLORS.hairline)};
  /* The selected ring is the border plus a 1px shadow ring in the same colour, so it reads 2px without
     shifting the card's content. */
  box-shadow: ${({ $selected }) => ($selected ? `0 0 0 1px ${COLORS.ctaSolid}` : 'none')};
  background: ${({ $selected }) => ($selected ? COLORS.surfaceFaint : COLORS.surfaceFainter)};
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition:
    border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    box-shadow ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    border-color: ${({ $selected }) => ($selected ? COLORS.ctaSolid : COLORS.hoverBorder)};
  }

  &:focus-visible {
    outline: none;
    border-color: ${COLORS.ctaSolid};
  }
`;

export const Thumb = styled.span`
  position: relative;
  display: block;
  aspect-ratio: 16 / 10;
  border-radius: 10px;
  overflow: hidden;
  background: ${COLORS.surfaceInset};
`;

const busyIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

/** Over a card whose style is being drawn (an extraction or its samples): the samples' dot field and the working Lumio
 *  mark, no words. Below the badges, which stay readable on top. */
export const BusyOverlay = styled.span`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  animation: ${busyIn} ${ART_STYLE.SAMPLE_FADE_MS}ms ${ANIM.EASE} both;

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

/* Over a thumbnail the backdrop lets it show faintly through; with none yet it is solid (the samples' backdrop). */
export const BusyBackdrop = styled.span<{ $solid: boolean }>`
  position: absolute;
  inset: 0;
  background: ${COLORS.surfaceCard};
  opacity: ${({ $solid }) => ($solid ? 1 : 0.82)};
`;

export const BusyMark = styled.span`
  position: relative;
  display: inline-flex;
`;

export const ThumbImg = styled.img<{ $loaded: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  opacity: ${({ $loaded }) => ($loaded ? 1 : 0)};
  transition: opacity ${ART_STYLE.SAMPLE_FADE_MS}ms ${ANIM.EASE};

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

/** No sample or mood image yet: a quiet palette mark on the inset surface. */
export const ThumbEmpty = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.textFaint};
`;

/** The look the website uses: its dot and word in the theme's primary orange, as the selected ring is. */
export const ActiveBadge = styled.span`
  position: absolute;
  top: ${SPACE[8]};
  left: ${SPACE[8]};
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[4]};
  padding: ${SPACE[2]} ${SPACE[8]};
  border-radius: 999px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceCard};
  color: ${COLORS.ctaSolid};
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
  box-shadow: ${COLORS.menuShadow};
`;

const breathe = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`;

export const ActiveDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: currentColor;
  animation: ${breathe} ${ANIM.LIVE_DOT_PULSE_MS}ms ease-in-out infinite;

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

/** A mature style's "18+" tag: the Active badge's chrome in the caution tone, in the opposite corner from it. */
export const MatureTag = styled.span`
  position: absolute;
  bottom: ${SPACE[8]};
  left: ${SPACE[8]};
  display: inline-flex;
  align-items: center;
  padding: ${SPACE[2]} ${SPACE[8]};
  border-radius: 999px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceCard};
  color: ${COLORS.danger};
  font-size: 11px;
  font-weight: 700;
  line-height: 1.4;
  box-shadow: ${COLORS.menuShadow};
`;

/** A catalog look the brand has no art in yet (or only some): a quiet tag in the corner opposite the 18+ one. */
export const ArtTag = styled.span`
  position: absolute;
  bottom: ${SPACE[8]};
  right: ${SPACE[8]};
  display: inline-flex;
  align-items: center;
  padding: ${SPACE[2]} ${SPACE[8]};
  border-radius: 999px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceCard};
  color: ${COLORS.textMuted};
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
  box-shadow: ${COLORS.menuShadow};
`;

export const Name = styled(Text)<{ $selected: boolean }>`
  display: block;
  padding: ${SPACE[8]} ${SPACE[4]} ${SPACE[4]};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 13px;
  font-weight: ${({ $selected }) => ($selected ? 600 : 500)};
  line-height: 1.35;
  color: ${({ $selected }) => ($selected ? COLORS.heading : COLORS.text)};
  transition: color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
`;

export const Untitled = styled(Name)`
  color: ${COLORS.textMuted};
`;

/** "+ New style": the strip's last card, a dashed outline of the same size. */
export const NewCard = styled.button<{ $lit?: boolean }>`
  ${cardBox}
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${SPACE[8]};
  box-sizing: border-box;
  min-height: 100%;
  padding: ${SPACE[12]};
  border-radius: 14px;
  border: 1px dashed ${COLORS.hoverBorder};
  background: transparent;
  color: ${COLORS.textMuted};
  cursor: pointer;
  transition:
    border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover:not(:disabled),
  &:focus-visible {
    outline: none;
    border-color: ${COLORS.ctaSolid};
    color: ${COLORS.text};
    background: ${COLORS.surfaceFainter};
  }

  /* Lit by the FTUX (the art style piece's New style step), it holds its hovered look. */
  ${({ $lit }) =>
    $lit &&
    css`
      border-color: ${COLORS.ctaSolid};
      color: ${COLORS.text};
      background: ${COLORS.surfaceFainter};
    `}

  &:disabled {
    cursor: progress;
    opacity: 0.6;
  }
`;

export const NewPlus = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 999px;
  background: ${COLORS.surfaceMuted};
`;

export const NewLabel = styled(Text)`
  font-size: 13px;
  font-weight: 600;
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const Spinner = styled.span`
  width: 14px;
  height: 14px;
  box-sizing: border-box;
  border-radius: 999px;
  border: 2px solid ${COLORS.hairline};
  border-top-color: ${COLORS.accent};
  animation: ${spin} ${ANIM.SPIN_MS}ms linear infinite;

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

export const ModalCopy = styled(Text)`
  display: block;
  font-size: 14px;
  line-height: 1.5;
  color: ${COLORS.textMuted};

  & + & {
    margin-top: ${SPACE[8]};
  }
`;

/**
 * An arrow over one end of the row, shown only while more cards lie past that end; it rides over the faded edge,
 * centred on the thumbnails. Phones get it too (beside the swipe), with a 48px touch target.
 */
export const EdgeArrow = styled.button<{ $side: 'start' | 'end'; $shown: boolean }>`
  position: absolute;
  z-index: 2;
  /* Centred on the thumbnail: the card's padding plus half its 16:10 thumb. */
  top: calc(${SPACE[8]} + (${ART_STYLE.CARD_WIDTH_PX}px - ${SPACE[8]}) * 10 / 32);
  ${({ $side }) => ($side === 'start' ? `left: ${SPACE[4]};` : `right: ${SPACE[4]};`)}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${ART_STYLE.STRIP_ARROW_PX}px;
  height: ${ART_STYLE.STRIP_ARROW_PX}px;
  padding: 0;
  border-radius: 999px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceCard};
  box-shadow: ${COLORS.menuShadow};
  color: ${COLORS.textBright};
  cursor: pointer;
  opacity: ${({ $shown }) => ($shown ? 1 : 0)};
  pointer-events: ${({ $shown }) => ($shown ? 'auto' : 'none')};
  /* It comes and goes with its end's fade: sliding in from the edge it points past, and out the same way. */
  transform: translateY(-50%)
    translateX(
      ${({ $shown, $side }) =>
        $shown ? 0 : $side === 'start' ? -ART_STYLE.STRIP_ARROW_SLIDE_PX : ART_STYLE.STRIP_ARROW_SLIDE_PX}px
    )
    scale(${({ $shown }) => ($shown ? 1 : 0.92)});
  transition:
    opacity ${ART_STYLE.STRIP_EDGE_MS}ms ${ANIM.EASE},
    transform ${ART_STYLE.STRIP_EDGE_MS}ms ${ANIM.EASE},
    border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  svg {
    transform: ${({ $side }) => ($side === 'end' ? 'scaleX(-1)' : 'none')};
  }

  /* A mouse only: a tap on a phone would leave the ring stuck on. */
  ${HOVER_CAPABLE} {
    &:hover {
      border-color: ${COLORS.ctaSolid};
    }
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.ctaSolid};
    outline-offset: 2px;
  }

  /* A touch target the app's 48px thumb size around the smaller drawn button. */
  &::after {
    content: '';
    position: absolute;
    inset: calc((48px - ${ART_STYLE.STRIP_ARROW_PX}px) / -2);
  }

  ${REDUCED_MOTION} {
    transition: none;
  }

  ${MOBILE} {
    /* Centred on a phone card's (narrower) thumbnail. */
    top: calc(${SPACE[8]} + (${ART_STYLE.CARD_WIDTH_MOBILE_PX}px - ${SPACE[8]}) * 10 / 32);
  }
`;
